import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heartbeat, Lightbulb, Moon, Barbell, ForkKnife, Pill } from '@phosphor-icons/react';
import { getHealthLogs, type HealthLogEntry, type HealthLogType } from '../../utils/healthLog';
import { fetchHealthInsights } from '../../utils/healthInsights';
import { computeCyclePrediction } from '../../utils/cyclePrediction';
import PhaseRing from '../cycle/PhaseRing';
import CycleStatusText from '../cycle/CycleStatusText';
import { CYCLE_EXPERIENCES } from '@researcher/shared';
import type { HealthInsight } from '@researcher/shared';

const MOOD_EMOJI: Record<string, string> = {
  great: '😊',
  good: '🙂',
  okay: '😐',
  low: '😔',
  bad: '😢',
};

const SEVERITY_DOT: Record<number, string> = {
  1: 'bg-green-400',
  2: 'bg-lime-400',
  3: 'bg-yellow-400',
  4: 'bg-orange-400',
  5: 'bg-red-400',
};

const experienceMap = Object.fromEntries(
  CYCLE_EXPERIENCES.map((e) => [e.key, e])
);

interface Props {
  refreshTrigger?: number;
}

const TYPE_DOT: Record<HealthLogType, string> = {
  cycle: 'bg-pink-400',
  health: 'bg-blue-400',
  sleep: 'bg-indigo-400',
  nutrition: 'bg-amber-400',
  exercise: 'bg-teal-400',
  intimate: 'bg-purple-400',
};

const TYPE_LABEL: Record<HealthLogType, string> = {
  cycle: 'Cycle',
  health: 'Health',
  sleep: 'Sleep',
  nutrition: 'Nutrition',
  exercise: 'Exercise',
  intimate: 'Intimate',
};

const TYPE_LINK: Record<HealthLogType, string> = {
  cycle: '/health/cycle',
  health: '/health',
  sleep: '/health/sleep',
  nutrition: '/health/nutrition',
  exercise: '/health/activity',
  intimate: '/health/intimate',
};

export default function HealthSummaryPanel({ refreshTrigger = 0 }: Props) {
  const [entries, setEntries] = useState<HealthLogEntry[]>([]);
  const [cycleEntries, setCycleEntries] = useState<HealthLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [topInsight, setTopInsight] = useState<HealthInsight | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [page, cyclePage, insights] = await Promise.all([
          getHealthLogs({ limit: 8 }),
          getHealthLogs({ type: 'cycle', limit: 10 }),
          fetchHealthInsights().catch(() => null),
        ]);
        setEntries(page.items);
        setCycleEntries(cyclePage.items);
        if (insights?.insights?.length) {
          setTopInsight(insights.insights[0]);
        }
      } catch (err) {
        console.error('Error loading health summary:', err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [refreshTrigger]);

  const cyclePrediction = useMemo(
    () => computeCyclePrediction(cycleEntries),
    [cycleEntries]
  );

  if (isLoading) {
    return (
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center space-x-2 mb-2">
          <Heartbeat size={16} className="text-pink-500" weight="fill" />
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Health</span>
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500">Loading...</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center space-x-2 mb-2">
          <Heartbeat size={16} className="text-pink-500" weight="fill" />
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Health</span>
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Chat about your health and entries will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Heartbeat size={16} className="text-pink-500" weight="fill" />
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Health Tracker</span>
        </div>
        <Link
          to="/health"
          className="text-xs text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300"
        >
          View all
        </Link>
      </div>

      {/* Cycle ring section */}
      {cyclePrediction && (
        <Link
          to="/health/cycle"
          className="flex items-center gap-3 mb-3 p-2.5 bg-pink-50/80 dark:bg-pink-950/20 border border-pink-100 dark:border-pink-900/40 rounded-lg hover:bg-pink-100/60 dark:hover:bg-pink-950/30 transition-colors"
        >
          <div className="w-11 h-11 flex-shrink-0">
            <PhaseRing prediction={cyclePrediction} className="w-full h-full" hideText />
          </div>
          <div className="flex-1 min-w-0">
            <CycleStatusText
              prediction={cyclePrediction}
              titleClassName="text-xs font-semibold text-gray-800 dark:text-gray-100"
              subtitleClassName="text-[10px] text-gray-500 dark:text-gray-400"
            />
          </div>
        </Link>
      )}

      {topInsight && (
        <Link
          to="/health"
          className="flex items-center gap-1.5 mb-2 px-2.5 py-1.5 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800/30 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
        >
          <Lightbulb size={12} weight="fill" className="text-orange-500 flex-shrink-0" />
          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
            {topInsight.headline}
          </span>
        </Link>
      )}
      <div className="space-y-1.5 max-h-52 overflow-y-auto">
        {entries.map((entry) => {
          const isCycle = entry.type === 'cycle';
          const isSleep = entry.type === 'sleep';
          const isExercise = entry.type === 'exercise';
          const isNutrition = entry.type === 'nutrition';
          const topSymptom = entry.symptoms?.[0];
          const firstNonBleeding = isCycle
            ? entry.cycleExperiences?.find(Boolean)
            : undefined;
          const topExperience = firstNonBleeding
            ? experienceMap[firstNonBleeding]
            : null;

          return (
            <Link
              key={entry.id}
              to={TYPE_LINK[entry.type] || '/health'}
              className="block bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-2 text-xs transition-colors"
            >
              <div className="flex items-center justify-between mb-0.5">
                <div className="flex items-center space-x-1.5">
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${TYPE_DOT[entry.type] || 'bg-gray-400'}`}
                    title={TYPE_LABEL[entry.type]}
                  />
                  <span className="text-gray-400 dark:text-gray-500 text-[10px]">
                    {new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                    {TYPE_LABEL[entry.type]}
                  </span>
                </div>
                {entry.mood && (
                  <span title={`Mood: ${entry.mood}`}>{MOOD_EMOJI[entry.mood]}</span>
                )}
              </div>

              {/* Cycle entry */}
              {isCycle && (() => {
                const hasBleeding = entry.flowLevel ? entry.flowLevel !== 'none' : entry.bleeding;
                const flowLabel = entry.flowLevel === 'spotting' ? '💧 Spotting' : '🩸 Bleeding';
                return (
                  <div className="flex items-center space-x-1.5">
                    {hasBleeding && (
                      <span className="text-pink-600 dark:text-pink-400 font-medium">{flowLabel}</span>
                    )}
                    {!hasBleeding && topExperience && (
                      <span className="text-pink-600 dark:text-pink-400 font-medium">
                        {topExperience.emoji} {topExperience.label}
                      </span>
                    )}
                    {hasBleeding && topExperience && (
                      <span className="text-gray-400 dark:text-gray-500">
                        · {topExperience.emoji} {topExperience.label}
                      </span>
                    )}
                    {!hasBleeding && !topExperience && entry.cyclePhase && (
                      <span className="text-pink-600 dark:text-pink-400 font-medium capitalize">{entry.cyclePhase}</span>
                    )}
                  </div>
                );
              })()}

              {/* Health entry: symptoms + medications */}
              {entry.type === 'health' && (
                <div className="flex items-center space-x-1.5">
                  {topSymptom ? (
                    <>
                      {topSymptom.severity && (
                        <span
                          className={`w-2 h-2 rounded-full ${SEVERITY_DOT[topSymptom.severity] || 'bg-gray-300'}`}
                        />
                      )}
                      <span className="text-gray-700 dark:text-gray-300 font-medium">{topSymptom.name}</span>
                      {entry.symptoms.length > 1 && (
                        <span className="text-gray-400 dark:text-gray-500">+{entry.symptoms.length - 1}</span>
                      )}
                    </>
                  ) : entry.medications?.length > 0 ? (
                    <>
                      <Pill size={12} className="text-rose-400 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300 font-medium truncate">{entry.medications[0]}</span>
                      {entry.medications.length > 1 && (
                        <span className="text-gray-400 dark:text-gray-500">+{entry.medications.length - 1}</span>
                      )}
                    </>
                  ) : entry.cyclePhase ? (
                    <span className="text-pink-600 dark:text-pink-400 font-medium capitalize">{entry.cyclePhase}</span>
                  ) : null}
                </div>
              )}

              {/* Sleep entry */}
              {isSleep && (
                <div className="flex items-center space-x-1.5">
                  <Moon size={12} className="text-indigo-400 flex-shrink-0" />
                  {entry.sleepHours ? (
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{entry.sleepHours}h sleep</span>
                  ) : null}
                  {entry.sleepQuality ? (
                    <span className="text-gray-400 dark:text-gray-500">· quality {entry.sleepQuality}/5</span>
                  ) : null}
                </div>
              )}

              {/* Exercise entry */}
              {isExercise && entry.workouts?.[0] && (
                <div className="flex items-center space-x-1.5">
                  <Barbell size={12} className="text-teal-400 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 font-medium truncate">{entry.workouts[0].type}</span>
                  <span className="text-gray-400 dark:text-gray-500">{entry.workouts[0].durationMinutes}min</span>
                  {entry.workouts.length > 1 && (
                    <span className="text-gray-400 dark:text-gray-500">+{entry.workouts.length - 1}</span>
                  )}
                </div>
              )}

              {/* Nutrition entry */}
              {isNutrition && (
                <div className="flex items-center space-x-1.5">
                  <ForkKnife size={12} className="text-amber-400 flex-shrink-0" />
                  {entry.meals?.length ? (
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{entry.meals.length} meal{entry.meals.length !== 1 ? 's' : ''}</span>
                  ) : null}
                  {entry.waterIntake ? (
                    <span className="text-gray-400 dark:text-gray-500">· {entry.waterIntake} cups water</span>
                  ) : null}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
